import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { LayoutDashboard, Loader2 } from 'lucide-react';
import 'react-grid-layout/css/styles.css';

import { getPublicDashboard } from '../../services/dashboardApi';
import DashboardWidget from '../../components/dashboards/DashboardWidget';
import { buildLayouts } from '../../components/dashboards/layout';

const Grid = WidthProvider(Responsive);

// While a shared snapshot is open: keep it out of search results and keep the
// token in the URL from leaking to other sites through the Referer header.
const PRIVATE_META = { robots: 'noindex, nofollow', referrer: 'no-referrer' };

export const usePrivatePage = () =>
  useEffect(() => {
    const added = Object.entries(PRIVATE_META).map(([name, content]) => {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      document.head.appendChild(meta);
      return meta;
    });
    return () => added.forEach((meta) => meta.remove());
  }, []);

/** /d/:token — a shared snapshot, read-only, no sign-in, no app chrome. */
const PublicDashboardPage = () => {
  const { token } = useParams();
  const [shared, setShared] = useState(null);
  const [error, setError] = useState(null);
  usePrivatePage();

  useEffect(() => {
    let cancelled = false;
    getPublicDashboard(token)
      .then((res) => !cancelled && setShared(res.data))
      .catch(
        (e) => !cancelled && setError(e?.message || 'This link does not work.'),
      );
    return () => {
      cancelled = true;
    };
  }, [token]);

  const layouts = useMemo(
    () => (shared ? buildLayouts(shared.spec) : null),
    [shared],
  );

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-[#0a0a0c] px-4 text-center text-zinc-300">
        <LayoutDashboard size={28} className="text-zinc-600" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }
  if (!shared) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0a0a0c]">
        <Loader2 className="animate-spin text-zinc-500" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#0a0a0c] text-zinc-200">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-zinc-800 px-4 py-3">
        <h1 className="text-sm font-semibold text-zinc-100">{shared.title}</h1>
        <span className="text-[11px] text-zinc-500">
          Snapshot from {new Date(shared.snapshot_at).toLocaleString()}
        </span>
      </header>
      <main className="flex-1 px-2 py-2 md:px-4">
        <Grid
          layouts={layouts}
          breakpoints={{ lg: 768, sm: 0 }}
          cols={{ lg: 12, sm: 1 }}
          rowHeight={80}
          margin={[12, 12]}
          containerPadding={[0, 0]}
          isDraggable={false}
          isResizable={false}
        >
          {shared.spec.widgets.map((w) => (
            <div key={w.id}>
              {/* A widget missing from the snapshot was added after it was taken. */}
              <DashboardWidget
                widget={w}
                data={shared.widgets[w.id] || { rows: [] }}
              />
            </div>
          ))}
        </Grid>
      </main>
    </div>
  );
};

export default PublicDashboardPage;
